/*
 * TunePad
 *
 * Michael S. Horn
 * Northwestern University
 * michael-horn@northwestern.edu
 *
 * This project was funded by the National Science Foundation (grant DRL-1612619).
 * Any opinions, findings and conclusions or recommendations expressed in this
 * material are those of the author(s) and do not necessarily reflect the views
 * of the National Science Foundation (NSF).
 */
import html from './waveform.module.html';

import { TimelineMidi } from './midi';
import { Synthesizer, WaveformLoadingService, WaveformData } from '../../audio';


/**
 * Timeline that can show waveform view of audio
 * 
 * <timeline-waveform
 *     playhead = "0"           // location of the playhead in beats
 *     theme = "dark | light"   // default is dark
 *     instrument = "piano">    // changes the color of the visualization
 */
export class TimelineWaveform extends TimelineMidi {

    static readonly ELEMENT = "timeline-waveform";

    static _TIMELINE_ID = 0;

    private uuid : string = `${TimelineWaveform._TIMELINE_ID++}`;

    /** used to generate waveform data */
    private synth = new Synthesizer();
    private waveform = new WaveformData();



    constructor() {
        super();
        this.root.innerHTML = html;

        this.container = this.root.querySelector('#container') as HTMLDivElement;
        this.scrollContainer = this.root.querySelector('#scroll-container') as HTMLDivElement;
        this.svg = this.root.querySelector('#timeline') as SVGSVGElement;
    }

    onTempoChange() { this.regenerateWaveform(); }

    protected render() {
        super.render();
        this.regenerateWaveform();
    }


    /**
     * Creates SVG path that visualizes the waveform
     */
    protected renderData() {
        this._dataGroup.innerHTML = '';
        this._dataGroup.setAttribute(
            "transform",
            `translate(0, ${this.rulerHeight + this.scoreHeight / 2}), ` +
            `scale(${this.pixelsPerSecond}, ${this.scoreHeight / 2})`
        );
        const path = this.waveform.wavepath;
        if (path) {
            path.classList.add("timeline-mark");
            this._dataGroup.append(path);
        }

    }


    private async regenerateWaveform() {
        this.waveform = new WaveformData();
        this.synth.bpm = this.audio.bpm;

        // load synth patch
        if (this.trace.patch) {
            await this.synth.loadPatch(this.trace.patch);
        }

        WaveformLoadingService.cancelRequest(this.uuid);
        WaveformLoadingService.requestWaveform({
            uuid : this.uuid,
            beats : this.beats,
            trace : this.trace,
            synth : this.synth,
            force : true,
            increment : this.measure,
            waveform : this.waveform,
            progress : () => { this.renderData() },
        })
        .then((waveform) => { this.renderData(); });
    }
}
